import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Shield, CheckCircle, Clock, AlertTriangle, Upload, FileText,
  User, Building2, Globe, Lock, Eye, XCircle
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KYCPage = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [entityType, setEntityType] = useState("individual");
  const [jurisdiction, setJurisdiction] = useState("UAE");
  const [verificationLevel, setVerificationLevel] = useState("basic");

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchKYCStatus();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const res = await axios.get(`${API}/kyc/status`, { headers });
      setKycStatus(res.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const submitKYC = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/kyc/submit`, {
        entity_type: entityType,
        jurisdiction,
        verification_level: verificationLevel,
        documents: []
      }, { headers });
      
      toast({ title: "KYC Submitted", description: "Your verification is under review" });
      fetchKYCStatus();
    } catch (e) {
      toast({ title: "Error", description: e.response?.data?.detail || "Submission failed", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved": return <CheckCircle className="h-6 w-6 text-green-500" />;
      case "in_review": return <Clock className="h-6 w-6 text-amber-500" />;
      case "rejected": return <XCircle className="h-6 w-6 text-red-500" />;
      default: return <AlertTriangle className="h-6 w-6 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved": return "bg-green-100 text-green-800 border-green-200";
      case "in_review": return "bg-amber-100 text-amber-800 border-amber-200";
      case "rejected": return "bg-red-100 text-red-800 border-red-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Header />

      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-8">
        <div className="container mx-auto px-6">
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Shield className="h-8 w-8" />
            KYC / AML Compliance
          </h1>
          <p className="opacity-90 mt-1">Identity verification and anti-money laundering compliance</p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Current Status Card */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Verification Status
              </CardTitle>
              <Badge className={getStatusColor(kycStatus?.status)}>
                {kycStatus?.status?.replace('_', ' ').toUpperCase() || 'NOT STARTED'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              {getStatusIcon(kycStatus?.status)}
              <div className="flex-1">
                <p className="font-semibold">
                  {kycStatus?.status === 'approved' && "Verification Complete"}
                  {kycStatus?.status === 'in_review' && "Under Review"}
                  {kycStatus?.status === 'rejected' && "Verification Failed"}
                  {kycStatus?.status === 'not_started' && "Verification Required"}
                </p>
                <p className="text-sm text-gray-500">
                  {kycStatus?.status === 'approved' && `Valid until ${kycStatus?.expiry_date}`}
                  {kycStatus?.status === 'in_review' && "Usually takes 24-48 hours"}
                  {kycStatus?.status === 'rejected' && kycStatus?.reviewer_notes}
                  {kycStatus?.status === 'not_started' && "Complete KYC to access all platform features"}
                </p>
              </div>
            </div>

            {kycStatus?.status === 'approved' && (
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-medium">ID Verified</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-medium">Address Verified</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-medium">AML Check Passed</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit KYC Form */}
        {kycStatus?.status !== 'approved' && kycStatus?.status !== 'in_review' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Submit KYC Application
              </CardTitle>
              <CardDescription>
                Complete your identity verification to access all platform features
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label>Entity Type</Label>
                  <Select value={entityType} onValueChange={setEntityType}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4" /> Individual
                        </div>
                      </SelectItem>
                      <SelectItem value="company">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4" /> Company
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Jurisdiction</Label>
                  <Select value={jurisdiction} onValueChange={setJurisdiction}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UAE">UAE</SelectItem>
                      <SelectItem value="Saudi Arabia">Saudi Arabia</SelectItem>
                      <SelectItem value="Qatar">Qatar</SelectItem>
                      <SelectItem value="Kuwait">Kuwait</SelectItem>
                      <SelectItem value="Bahrain">Bahrain</SelectItem>
                      <SelectItem value="Oman">Oman</SelectItem>
                      <SelectItem value="USA">United States</SelectItem>
                      <SelectItem value="UK">United Kingdom</SelectItem>
                      <SelectItem value="EU">European Union</SelectItem>
                      <SelectItem value="Singapore">Singapore</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Verification Level</Label>
                <div className="grid grid-cols-3 gap-4 mt-2">
                  {[
                    { value: "basic", label: "Basic", desc: "ID & Address", icon: User },
                    { value: "enhanced", label: "Enhanced", desc: "+ Source of Funds", icon: Shield },
                    { value: "institutional", label: "Institutional", desc: "Full Corporate DD", icon: Building2 }
                  ].map((level) => (
                    <div
                      key={level.value}
                      onClick={() => setVerificationLevel(level.value)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        verificationLevel === level.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <level.icon className={`h-6 w-6 mb-2 ${
                        verificationLevel === level.value ? 'text-blue-600' : 'text-gray-400'
                      }`} />
                      <p className="font-semibold">{level.label}</p>
                      <p className="text-xs text-gray-500">{level.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Required Documents */}
              <div>
                <Label className="mb-3 block">Required Documents</Label>
                <div className="space-y-3">
                  {[
                    "Government-issued ID (Passport, Emirates ID)",
                    "Proof of Address (Utility bill, Bank statement)",
                    entityType === "company" && "Company Registration Documents",
                    entityType === "company" && "Shareholder Registry",
                    verificationLevel !== "basic" && "Source of Funds Declaration"
                  ].filter(Boolean).map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <span className="text-sm">{doc}</span>
                      </div>
                      <Button size="sm" variant="outline">
                        <Upload className="h-4 w-4 mr-1" /> Upload
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex gap-3">
                  <Lock className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-800">Secure & Confidential</p>
                    <p className="text-sm text-amber-700">
                      Your documents are encrypted and stored securely. We comply with GDPR, CCPA, and UAE data protection regulations.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={submitKYC}
                disabled={submitting}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 py-6 text-lg"
              >
                {submitting ? "Submitting..." : "Submit KYC Application"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Compliance Info */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Multi-Jurisdiction Compliance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { region: "UAE", status: "Active" },
                { region: "GCC", status: "Active" },
                { region: "EU", status: "Active" },
                { region: "USA", status: "Coming Soon" }
              ].map((item, idx) => (
                <div key={idx} className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="font-semibold">{item.region}</p>
                  <Badge variant={item.status === "Active" ? "default" : "secondary"} className="mt-1">
                    {item.status}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KYCPage;
